/** Small formatting helpers for the offline UI. */
import type { MessageKey, T } from '../i18n';
import { SavePackError } from './manager';
import type { PackView, SongOfflineState } from './store';
import type { PackRow } from './types';

/** "1,2 GB" / "340 MB" / "820 kB" in the UI locale (decimal units). */
export function formatSize(bytes: number, loc: string): string {
  const nf = (max: number) => new Intl.NumberFormat(loc, { maximumFractionDigits: max });
  if (bytes >= 1e9) return `${nf(bytes >= 1e10 ? 0 : 1).format(bytes / 1e9)} GB`;
  if (bytes >= 1e6) return `${nf(bytes >= 1e8 ? 0 : 1).format(bytes / 1e6)} MB`;
  if (bytes >= 1e3) return `${nf(0).format(bytes / 1e3)} kB`;
  return `${nf(0).format(bytes)} B`;
}

export function percent(done: number, total: number): number {
  if (total <= 0) return 0;
  return Math.max(0, Math.min(100, Math.floor((done / total) * 100)));
}

/** Display title of a pack ("allt mitt" has no stored title). */
export function packTitle(t: T, row: Pick<PackRow, 'kind' | 'title'>): string {
  if (row.kind === 'all') return t('offline.all_title');
  return row.title || t(row.kind === 'song' ? 'offline.kind.song' : 'offline.kind.collection');
}

const STATUS_KEY: Record<PackView['status'], MessageKey> = {
  pending: 'offline.status.pending',
  downloading: 'offline.status.downloading',
  complete: 'offline.status.complete',
  update: 'offline.status.update',
  error: 'offline.status.error',
};

export function packStatusText(t: T, view: PackView): string {
  return t(STATUS_KEY[view.status]);
}

const KNOWN_ERRORS = new Set(['offline', 'gone', 'storage', 'network', 'storage_full', 'unsupported', 'server', 'invalid']);

/** Text for a pack error code or a thrown SavePackError. */
export function offlineErrorText(t: T, codeOrError: unknown): string {
  const code = codeOrError instanceof SavePackError ? codeOrError.code : typeof codeOrError === 'string' ? codeOrError : null;
  if (code && KNOWN_ERRORS.has(code)) return t(`offline.error.${code}` as MessageKey);
  return t('common.error');
}

export const BADGE_KEY: Record<SongOfflineState, MessageKey> = {
  saved: 'offline.badge.saved',
  downloading: 'offline.badge.downloading',
  update: 'offline.badge.update',
  pending: 'offline.badge.pending',
  error: 'offline.badge.error',
};
